import React, { FC, useEffect } from "react";
import { useAtom } from "jotai";
import { BrowserRouter as Router, Route } from "react-router-dom";
import * as Sentry from "@sentry/react";

import {
  AppCrashFallback,
  GlobalHelmet,
  GlobalStyles,
  LoadingScreen,
  Tracking,
} from "@shared/components";
import { useChangeOrg, useUser, useUserOrgs } from "@shared/hooks";
import { orgAtom } from "@shared/state";
import { loadOrg } from "@shared/utils";

import { AppProvider } from "./providers/AppProvider";
import { AppRoutes } from "./routes/AppRoutes";

import "@mantine/core/styles.layer.css";
import "@mantine/spotlight/styles.layer.css";
import "@mantine/code-highlight/styles.layer.css";
import "@mantine/notifications/styles.layer.css";
import "@mantine/dates/styles.layer.css";
import "@mantine/charts/styles.layer.css";
import "mantine-contextmenu/styles.layer.css";

if (process.env.NODE_ENV === "development") {
  import("@locator/runtime").then(({ setup }) => {
    setup({});
  });
}

/**
 * We add the react-router outside the App element, so that calling
 * history.go(0) re-renders the whole <App />.
 *
 */
const App: FC = () => {
  const [org] = useAtom(orgAtom);
  const [user] = useUser();
  const { data: userOrgs, isLoading: isUserOrgsLoading } = useUserOrgs();
  const changeOrg = useChangeOrg();

  useEffect(() => {
    if (!org && user && !isUserOrgsLoading && userOrgs) {
      const loadedOrg = loadOrg(user, userOrgs);
      if (loadedOrg) {
        changeOrg(loadedOrg);
      }
    }
  }, [org, changeOrg, user, userOrgs, isUserOrgsLoading]);

  return (
    <Router>
      <GlobalStyles />
      <GlobalHelmet />
      <AppProvider>
        <Route path="/">
          <Tracking />
        </Route>
        <React.Suspense fallback={<LoadingScreen />}>
          <AppRoutes />
        </React.Suspense>
      </AppProvider>
    </Router>
  );
};

export default Sentry.withErrorBoundary(App, {
  showDialog: true,
  fallback: (props) => <AppCrashFallback {...props} />,
});
